import { useSyncExternalStore } from 'react'
import type { EventStore } from '@bloo-team/api-client'

/**
 * Storage demo overlay — localStorage v2.
 *
 * Demo mode has no backend; the stream fixtures (`DEMO_STREAMS`) and the
 * archive fixtures (`DEMO_EVENT_STORES`) are stateless module constants.
 * Writes on the Storage page therefore land in an OVERLAY applied over the
 * fixtures on read, so a demo session behaves like a tenant: a retention
 * edit sticks across reloads, a deleted archive stays gone, an uploaded one
 * lists. Live mode never touches this — the backend is the state.
 *
 * THIS SHAPE IS A DRAFT API CONTRACT (docs/storage.md §Demo store): each
 * overlay field is the console-side echo of one write —
 *   `retention.cluster`      ↔ PUT mgmt/core/stream-config           (R1PUT)
 *   `retention.scope`        ↔ PUT mgmt/scope/{scopeId}/stream-config (R2)
 *   `archives[name].deleted` ↔ event_store/delete                    (A1)
 *   `archives[name].created` ↔ event_store/upload                    (A2)
 *
 * v1 → v2: v1 kept `retention` as a per-stream map (`retention[stream]`),
 * built on the assumption that stream-config was per stream. The R1GET
 * capture (docs/inventory/storage-capture.json) showed ONE cluster-wide
 * object — that is why every stream-management row reports 90/1 — with an
 * optional scope override. So v2's `retention` has exactly two optional
 * slots, `cluster` and `scope`. A v1 blob is migrated by DROPPING its
 * per-stream map: only the `cluster` / `scope` keys are read, anything else
 * under `retention` is ignored, and the next write persists the v2 shape.
 * The archives part is unchanged between the versions.
 *
 * Storage: ONE key per cluster+scope, `bloo.storage.<clusterId>.<scopeId>`.
 * House localStorage rules (cf. extractor-store): try/catch everywhere,
 * SSR-safe, a corrupt blob is superseded by the next write rather than
 * crashing the page.
 */

/** The two levels stream-config exists at. */
export type RetentionLevel = 'cluster' | 'scope'

export const RETENTION_LEVELS: readonly RetentionLevel[] = ['cluster', 'scope'] as const

export interface RetentionOverlay {
  /** Days; null = cleared. */
  retention: number | null
  replication: number | null
  /** Epoch ms of the change — the Retention tab's "last changed". */
  at: number
}

/** One optional entry per level; an absent level means "fixture value". */
export type RetentionLevels = Partial<Record<RetentionLevel, RetentionOverlay>>

export interface ArchiveOverlay {
  deleted?: true
  /** A row that does not exist in the fixtures — the echo of an upload. */
  created?: EventStore
  at: number
}

export interface StorageOverlay {
  retention: RetentionLevels
  archives: Record<string, ArchiveOverlay>
}

export const STORAGE_KEY_PREFIX = 'bloo.storage.'

export function storageOverlayKey(clusterId: string, scopeId: string): string {
  return `${STORAGE_KEY_PREFIX}${clusterId}.${scopeId}`
}

export interface StorageOverlayStore {
  /** Stable snapshot until the next mutation. */
  all: () => StorageOverlay
  /** Echo of `PUT stream-config` at one level. An omitted field keeps its current override. */
  setRetention: (level: RetentionLevel, next: { retention?: number | null; replication?: number | null }) => void
  /** Forget one level's override — back to the fixture (cluster) or to "no override" (scope). */
  clearRetention: (level: RetentionLevel) => void
  /** Echo of `event_store/delete`. Archive names (the row's key). */
  markDeleted: (names: string[]) => void
  /** Echo of `event_store/upload` — a row `applyArchiveOverlay` appends, keyed by its `name`. */
  addArchive: (row: EventStore) => void
  /** Drop every override — demo back to fixture state. */
  reset: () => void
  subscribe: (cb: () => void) => () => void
}

const EMPTY: StorageOverlay = Object.freeze({
  retention: Object.freeze({}) as RetentionLevels,
  archives: Object.freeze({}) as Record<string, ArchiveOverlay>,
}) as StorageOverlay

const numOrNull = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null)

/** An `EventStore` from storage, or undefined when it is not one. Keyed by `name` (A0: the row has no id). */
function readEventStore(v: unknown): EventStore | undefined {
  if (!v || typeof v !== 'object') return undefined
  const o = v as Record<string, unknown>
  if (typeof o.name !== 'string' || o.name.length === 0) return undefined
  const str = (k: string): string | null => (typeof o[k] === 'string' ? (o[k] as string) : null)
  const dataSource = str('dataSource')
  return {
    name: o.name,
    author: str('author'),
    createdBy: str('createdBy'),
    userId: str('userId'),
    dataSource,
    sourceKind: o.sourceKind === 'upload' || (o.sourceKind !== 'dql' && /^File Upload/i.test(dataSource ?? '')) ? 'upload' : 'dql',
    extension: str('extension'),
    fileName: str('fileName'),
    createdAt: numOrNull(o.createdAt),
    modifiedBy: str('modifiedBy'),
    modifiedAt: numOrNull(o.modifiedAt),
    scopeId: str('scopeId'),
  }
}

/** Parse a stored blob defensively: unknown keys dropped, bad entries skipped. */
export function readStorageOverlay(parsed: unknown): StorageOverlay {
  const out: StorageOverlay = { retention: {}, archives: {} }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return out
  const root = parsed as Record<string, unknown>
  const ret = root.retention
  if (ret && typeof ret === 'object' && !Array.isArray(ret)) {
    // v2 reads the two level slots only; a v1 per-stream map has stream
    // names here and is dropped (see the header).
    for (const level of RETENTION_LEVELS) {
      const v = (ret as Record<string, unknown>)[level]
      if (!v || typeof v !== 'object' || Array.isArray(v)) continue
      const o = v as Record<string, unknown>
      out.retention[level] = {
        retention: numOrNull(o.retention),
        replication: numOrNull(o.replication),
        at: numOrNull(o.at) ?? 0,
      }
    }
  }
  const arc = root.archives
  if (arc && typeof arc === 'object' && !Array.isArray(arc)) {
    for (const [id, v] of Object.entries(arc as Record<string, unknown>)) {
      if (!v || typeof v !== 'object') continue
      const o = v as Record<string, unknown>
      const entry: ArchiveOverlay = { at: numOrNull(o.at) ?? 0 }
      if (o.deleted === true) entry.deleted = true
      const created = readEventStore(o.created)
      if (created) entry.created = created
      out.archives[id] = entry
    }
  }
  return out
}

class LocalStorageOverlayStore implements StorageOverlayStore {
  private cache: StorageOverlay | null = null
  private listeners = new Set<() => void>()
  constructor(private readonly key: string) {}

  all = (): StorageOverlay => this.read()

  setRetention = (level: RetentionLevel, next: { retention?: number | null; replication?: number | null }) => {
    const cur = this.read()
    const prev = cur.retention[level]
    const entry: RetentionOverlay = {
      retention: next.retention !== undefined ? next.retention : (prev?.retention ?? null),
      replication: next.replication !== undefined ? next.replication : (prev?.replication ?? null),
      at: Date.now(),
    }
    this.write({ ...cur, retention: { ...cur.retention, [level]: entry } })
  }

  clearRetention = (level: RetentionLevel) => {
    const cur = this.read()
    if (!cur.retention[level]) return
    const { [level]: _gone, ...rest } = cur.retention
    this.write({ ...cur, retention: rest })
  }

  markDeleted = (names: string[]) => {
    const cur = this.read()
    const archives = { ...cur.archives }
    const at = Date.now()
    for (const name of names) archives[name] = { ...cur.archives[name], deleted: true, at }
    this.write({ ...cur, archives })
  }

  addArchive = (row: EventStore) => {
    const cur = this.read()
    const at = Date.now()
    const { deleted: _undeleted, ...rest } = cur.archives[row.name] ?? { at }
    this.write({ ...cur, archives: { ...cur.archives, [row.name]: { ...rest, created: row, at } } })
  }

  reset = () => {
    try {
      globalThis.localStorage?.removeItem(this.key)
    } catch {
      /* no storage: nothing to drop */
    }
    this.cache = EMPTY
    this.notify()
  }

  subscribe = (cb: () => void) => {
    this.listeners.add(cb)
    return () => {
      this.listeners.delete(cb)
    }
  }

  private read(): StorageOverlay {
    if (this.cache) return this.cache
    let parsed: unknown
    try {
      const raw = globalThis.localStorage?.getItem(this.key)
      parsed = raw ? JSON.parse(raw) : {}
    } catch {
      parsed = {}
    }
    this.cache = readStorageOverlay(parsed)
    return this.cache
  }

  private write(next: StorageOverlay) {
    try {
      globalThis.localStorage?.setItem(this.key, JSON.stringify(next))
    } catch {
      /* quota or no storage: keep the in-memory overlay so the UI still reflects the action */
    }
    this.cache = next
    this.notify()
  }

  private notify() {
    for (const cb of this.listeners) cb()
  }
}

const stores = new Map<string, StorageOverlayStore>()

/** One store per cluster+scope, shared by every hook and mutation on the page. */
export function storageStore(clusterId: string, scopeId: string): StorageOverlayStore {
  const key = storageOverlayKey(clusterId, scopeId)
  let s = stores.get(key)
  if (!s) {
    s = new LocalStorageOverlayStore(key)
    stores.set(key, s)
  }
  return s
}

/** Live view of the overlay for one scope. */
export function useStorageOverlay(clusterId: string, scopeId: string): StorageOverlay {
  const store = storageStore(clusterId, scopeId)
  return useSyncExternalStore(store.subscribe, store.all, store.all)
}

/** Demo seeder body: forget every storage overlay in this browser. Idempotent. */
export function resetAllStorageOverlays(): void {
  const ls = globalThis.localStorage
  if (ls) {
    try {
      for (let i = ls.length - 1; i >= 0; i--) {
        const key = ls.key(i)
        if (key && key.startsWith(STORAGE_KEY_PREFIX)) ls.removeItem(key)
      }
    } catch {
      /* no storage */
    }
  }
  for (const s of stores.values()) s.reset()
}
